/**
 * Brand editor — full-page form with live preview pane. Used both for
 * `/brands/new` (no `id` param) and `/brands/:id`.
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { Link as RouterLink, useLoaderData, useNavigate, useParams } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { bundleFileName } from '@/api/admin/brandBundle';
import { DEFAULT_RETRY_AFTER_SECONDS } from '@/api/client';
import { ApiError } from '@/api/errors';
import type { BrandDto, BrandTemplate, ProductDto, TenantDto } from '@/api/types';
import { BRAND_TEMPLATES } from '@/api/types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ErrorBanner } from '@/components/ErrorBanner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { getProducts } from '@/api/lookupCache';
import { env } from '@/config/env';
import { BrandImageField } from './BrandImageField';
import { BrandAssetSlots } from './BrandAssetSlots';
import { BrandBundleDialog } from './BrandBundleDialog';
import { BrandEditorTabs } from './BrandEditorTabs';
import { GuestSignupHint } from './GuestSignupHint';
import type { EditorState } from './types';
import { SUPPORTED_PROVIDERS } from './types';

const SLUG_REGEX = /^[a-z0-9-]+$/;

// Spec 0016 — reserved copyStrings keys that have dedicated marketing fields
// Spec 0018 — extended to include login_title
// SASADM-0155 — and welcomeHeadline
const RESERVED_COPY_KEYS = [
  'shortName',
  'tagline',
  'heroCopy',
  'location',
  'login_title',
  'welcomeHeadline',
] as const;

// Spec 0018 Part A — template→fields mapping (grounded in saas_login 2026-06-06)
// All templates use: logo, favicon, colors, fonts, loginTitle, shortName, guest-signup toggle
type TemplateField = 'heroImage' | 'tagline' | 'heroCopy' | 'location';
const TEMPLATE_FIELDS: Record<BrandTemplate | '', TemplateField[]> = {
  '': [], // centered (default) — uses none of the extra fields
  centered: [],
  'split-hero': ['heroImage', 'tagline', 'heroCopy', 'location'],
  backdrop: ['heroImage'],
};

// SASADM-0107 U2 — one page of the `brandId`-filtered tenant read is the whole
// "tenants using this brand" list in practice (a brand's tenant set is small),
// so this asks for the server's max page size rather than paging the section.
// If the brand does exceed it, the section shows a "showing first N" caption.
const TENANTS_USING_SIZE = 200;

export interface BrandEditorLoaderData {
  brand: BrandDto | null;
  products: ProductDto[];
  tenantsUsing: TenantDto[];
  /**
   * How many tenants use this brand in total — `tenantsUsing.length` unless the
   * set is larger than one page. Absent (treated as 0) for a brand being
   * created, which reads no tenants at all.
   */
  tenantsUsingTotal?: number;
}

export async function brandEditorLoader({
  params,
}: {
  params: { id?: string };
}): Promise<BrandEditorLoaderData> {
  const products = await getProducts().catch(() => [] as ProductDto[]);
  if (!params.id || params.id === 'new') {
    return { brand: null, products, tenantsUsing: [] };
  }
  const id = Number(params.id);
  const brand = await apiAdmin.brands.get(id);
  // SASATH-0212 `brandId` filter — the server selects the tenants on this
  // brand, replacing a full-table fetch that was filtered client-side.
  const usingPage = await apiAdmin.tenants
    .listPaged({ brandId: id, size: TENANTS_USING_SIZE })
    .catch(() => null);
  const tenantsUsing = usingPage?.content ?? [];
  return {
    brand,
    products,
    tenantsUsing,
    tenantsUsingTotal: usingPage?.totalElements ?? tenantsUsing.length,
  };
}

/** A brand map's string values only (the editor never sends nested data back). */
function stringEntries(map: Record<string, unknown> | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(map ?? {})) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

function fromBrand(b: BrandDto | null, defaultProductId?: number): EditorState {
  const colors: Record<string, string> = b
    ? stringEntries(b.colors)
    : { primary: '#1d4ed8', background: '#ffffff', text: '#1a1a1a' };
  const fonts: Record<string, string> = b ? stringEntries(b.fonts) : {};
  const copyEntries = b
    ? Object.entries(b.copyStrings ?? {})
        .filter(([k, v]) => {
          const isReserved = (RESERVED_COPY_KEYS as readonly string[]).includes(k);
          return typeof v === 'string' && !isReserved;
        })
        .map(([key, value]) => ({ key, value: value as string }))
    : [];
  return {
    productId: b ? b.productId : (defaultProductId ?? ''),
    slug: b?.slug ?? '',
    displayName: b?.displayName ?? '',
    // Spec 0015 — editable image key/base pairs
    logoImage: b?.logoImage ?? '',
    logoBaseUrl: b?.logoBaseUrl ?? '',
    faviconImage: b?.faviconImage ?? '',
    faviconBaseUrl: b?.faviconBaseUrl ?? '',
    heroImage: b?.heroImage ?? '',
    heroBaseUrl: b?.heroBaseUrl ?? '',
    colors,
    fonts,
    copyEntries,
    authProviders: b?.authProviders ?? ['password'],
    signupEnabled: b?.signupEnabled ?? false,
    passwordResetEnabled: b?.passwordResetEnabled ?? true,
    // Spec 0016 fields
    template: (b?.template ?? '') as BrandTemplate | '',
    shortName: b?.shortName ?? '',
    tagline: b?.tagline ?? '',
    heroCopy: b?.heroCopy ?? '',
    location: b?.location ?? '',
    guestSignupEnabled: b?.guestSignupEnabled ?? false,
    primaryColorHover: b?.primaryColorHover ?? '',
    // Spec 0018 Part C — load login_title from copyStrings
    loginTitle: (b?.copyStrings?.login_title as string | undefined) ?? '',
    // SASADM-0155 — load welcomeHeadline from copyStrings
    welcomeHeadline: (b?.copyStrings?.welcomeHeadline as string | undefined) ?? '',
  };
}

function toCreateRequest(state: EditorState) {
  const copy: Record<string, string> = {};
  state.copyEntries.forEach((e) => {
    const trimmedKey = e.key.trim();
    // Spec 0016 — defensive: never send reserved keys in copyStrings map
    const isReserved = (RESERVED_COPY_KEYS as readonly string[]).includes(trimmedKey);
    if (trimmedKey && !isReserved) {
      copy[trimmedKey] = e.value;
    }
  });

  // Spec 0018 Part C — write loginTitle back to copyStrings.login_title
  const trimmedLoginTitle = state.loginTitle.trim();
  if (trimmedLoginTitle) {
    copy.login_title = trimmedLoginTitle;
  }

  // SASADM-0155 — write welcomeHeadline back to copyStrings.welcomeHeadline
  const trimmedWelcomeHeadline = state.welcomeHeadline.trim();
  if (trimmedWelcomeHeadline) {
    copy.welcomeHeadline = trimmedWelcomeHeadline;
  }

  return {
    productId: state.productId as number,
    slug: state.slug.toLowerCase(),
    displayName: state.displayName,
    // Spec 0015 — send editable image key/base pairs (no composed URLs)
    logoImage: state.logoImage || null,
    logoBaseUrl: state.logoBaseUrl || null,
    faviconImage: state.faviconImage || null,
    faviconBaseUrl: state.faviconBaseUrl || null,
    heroImage: state.heroImage || null,
    heroBaseUrl: state.heroBaseUrl || null,
    colors: state.colors,
    fonts: state.fonts,
    copyStrings: copy,
    authProviders: state.authProviders,
    signupEnabled: state.signupEnabled,
    passwordResetEnabled: state.passwordResetEnabled,
    // Spec 0016 — send null for empty strings so the backend clears them
    template: (state.template || null) as BrandTemplate | null,
    shortName: state.shortName || null,
    tagline: state.tagline || null,
    heroCopy: state.heroCopy || null,
    location: state.location || null,
    guestSignupEnabled: state.guestSignupEnabled,
    primaryColorHover: state.primaryColorHover || null,
  };
}

export function BrandEditor() {
  const { brand, products, tenantsUsing, tenantsUsingTotal } =
    useLoaderData() as BrandEditorLoaderData;
  const { t } = useCopy();
  useParams<{ id: string }>();
  const isNew = !brand;
  const navigate = useNavigate();
  const snack = useSnackbar();
  const [state, setState] = useState<EditorState>(() => fromBrand(brand));
  const [prevBrand, setPrevBrand] = useState(brand);
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Spec 0015 — separate upload state per image type
  const [logoUploadPending, setLogoUploadPending] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<ApiError | null>(null);
  const [faviconUploadPending, setFaviconUploadPending] = useState(false);
  const [faviconUploadError, setFaviconUploadError] = useState<ApiError | null>(null);
  const [heroUploadPending, setHeroUploadPending] = useState(false);
  const [heroUploadError, setHeroUploadError] = useState<ApiError | null>(null);
  // SASADM-0154 — bumped after a legacy save or an image upload: each changes
  // the theme etag, so the Theme tab re-GETs before its next save / when shown.
  const [themeStaleToken, setThemeStaleToken] = useState(0);
  const markThemeStale = () => setThemeStaleToken((n) => n + 1);
  // SASADM-0155 — bumped after a legacy save: the Translations tab re-GETs
  // copy-locales when next shown, so its defaults/placeholders are current.
  const [copyLocalesStaleToken, setCopyLocalesStaleToken] = useState(0);
  // SASADM-0159 — theme bundle Export / Import (behind VITE_BRAND_BUNDLE).
  const [bundleExporting, setBundleExporting] = useState(false);
  const [bundleDialogOpen, setBundleDialogOpen] = useState(false);

  // Re-derive editor state when the loaded brand changes via a render-phase
  // state adjustment (React's "adjust state while rendering" pattern) instead of
  // a synchronous setState in an effect — same result, no inter-render flash.
  if (brand !== prevBrand) {
    setPrevBrand(brand);
    setState(fromBrand(brand));
  }

  function update<K extends keyof EditorState>(key: K, value: EditorState[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }

  // Spec 0030 — the brand's product supplies the effective guest-signup URL/label
  // (read-only context for the toggle; the mock's link text).
  const effectiveProduct = products.find((p) => p.id === state.productId) ?? null;

  // Helper to compose a URL from image + baseUrl (or fall back to global if base is empty)
  function composeImageUrl(image: string, baseUrl: string): string {
    if (!image) return '';
    const effectiveBase = baseUrl || brand?.globalAssetBaseUrl || '';
    if (!effectiveBase) return '';
    const cleanBase = effectiveBase.endsWith('/') ? effectiveBase.slice(0, -1) : effectiveBase;
    return `${cleanBase}/${image}`;
  }

  function toggleProvider(p: string, on: boolean) {
    setState((s) => ({
      ...s,
      authProviders: on
        ? Array.from(new Set([...s.authProviders, p]))
        : s.authProviders.filter((x) => x !== p),
    }));
  }

  function setCopyEntry(idx: number, key: string, value: string) {
    setState((s) => ({
      ...s,
      copyEntries: s.copyEntries.map((e, i) => (i === idx ? { key, value } : e)),
    }));
  }

  async function onSave() {
    setError(null);
    if (state.slug.length > 0 && !SLUG_REGEX.test(state.slug)) {
      setError(new ApiError('UNKNOWN', 400, { details: { message: 'Invalid slug format' } }));
      return;
    }
    // Spec 0017 — block save if any copyEntries row has a reserved key
    const hasReservedKey = state.copyEntries.some((e) =>
      (RESERVED_COPY_KEYS as readonly string[]).includes(e.key.trim()),
    );
    if (hasReservedKey) {
      setError(new ApiError('RESERVED_COPY_KEY', 400));
      return;
    }
    setPending(true);
    try {
      if (isNew) {
        const body = toCreateRequest(state);
        const created = await apiAdmin.brands.create(body);
        snack.success(t('snackbar.brandCreated', 'Brand created.'));
        navigate(`/brands/${created.id}`);
      } else {
        // SASADM-0154 — the Theme tab owns colours and fonts. GET the brand
        // fresh right before the full-replace PUT and send its `colors`,
        // `fonts` and `primaryColorHover` back exactly as received, so this
        // save can never revert a theme save's write-backs.
        const fresh = await apiAdmin.brands.get(brand!.id);
        const body = toCreateRequest(state);
        const { productId: _drop, ...rest } = body;
        void _drop;
        await apiAdmin.brands.update(brand!.id, {
          ...rest,
          colors: stringEntries(fresh.colors),
          fonts: stringEntries(fresh.fonts),
          primaryColorHover: fresh.primaryColorHover ?? null,
        });
        markThemeStale();
        setCopyLocalesStaleToken((n) => n + 1);
        snack.success(t('snackbar.brandUpdated', 'Brand updated.'));
        navigate(`/brands/${brand!.id}`);
      }
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setPending(false);
    }
  }

  // SASADM-0154 — after a theme save, refetch the brand so the legacy state
  // (and the Preview tab's login mock) reflect the theme's colours/fonts
  // write-backs. Other unsaved form edits are kept.
  async function onThemeSaved() {
    if (!brand) return;
    try {
      const fresh = await apiAdmin.brands.get(brand.id);
      setState((s) => ({
        ...s,
        colors: stringEntries(fresh.colors),
        fonts: stringEntries(fresh.fonts),
        primaryColorHover: fresh.primaryColorHover ?? '',
      }));
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    }
  }

  // SASADM-0159 — download the bundle zip under §3.1's UTC name (the
  // Content-Disposition header isn't readable cross-origin).
  async function onExportBundle() {
    if (!brand) return;
    setBundleExporting(true);
    try {
      const blob = await apiAdmin.brandBundle.exportBundle(brand.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = bundleFileName(brand.slug, new Date());
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoked on the next tick so the click's download has taken the blob.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      if (!(err instanceof ApiError)) {
        snack.error(t('errors.codes.UNKNOWN', 'Something went wrong.'));
        return;
      }
      snack.error(
        err.status === 429
          ? t('brands.bundle.exportRateLimited', 'Too many exports — try again in {{n}} s', {
              n: err.details.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS,
            })
          : t(`errors.codes.${err.code}`, t('errors.generic', 'Something went wrong.')),
      );
    } finally {
      setBundleExporting(false);
    }
  }

  // SASADM-0159 — a bundle apply changed the theme, copy and images: the Theme
  // tab re-GETs (its stale token), the Translations tab re-GETs copy-locales,
  // and the brand is re-loaded so the header, legacy form, Preview mock and
  // image fields show the applied values (the same re-load as a legacy save).
  function onBundleApplied() {
    if (!brand) return;
    setBundleDialogOpen(false);
    markThemeStale();
    setCopyLocalesStaleToken((n) => n + 1);
    snack.success(t('brands.bundle.applied', 'Bundle applied'));
    navigate(`/brands/${brand.id}`);
  }

  async function onDelete() {
    if (!brand) return;
    setPending(true);
    try {
      await apiAdmin.brands.delete(brand.id);
      snack.success(t('snackbar.brandDeleted', 'Brand deleted.'));
      navigate('/brands');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err);
        setConfirmDelete(false);
      }
    } finally {
      setPending(false);
    }
  }

  // Spec 0015 — upload handlers for all three image types
  async function onUploadLogo(file: File) {
    if (!brand) return;
    setLogoUploadPending(true);
    setLogoUploadError(null);
    try {
      const updated = await apiAdmin.brands.uploadLogoImage(brand.id, file);
      setState((s) => ({
        ...s,
        logoImage: updated.logoImage ?? '',
        logoBaseUrl: updated.logoBaseUrl ?? '',
      }));
      markThemeStale();
      snack.success(t('snackbar.logoImageUploaded', 'Logo uploaded.'));
    } catch (err) {
      if (err instanceof ApiError) setLogoUploadError(err);
    } finally {
      setLogoUploadPending(false);
    }
  }

  async function onUploadFavicon(file: File) {
    if (!brand) return;
    setFaviconUploadPending(true);
    setFaviconUploadError(null);
    try {
      const updated = await apiAdmin.brands.uploadFaviconImage(brand.id, file);
      setState((s) => ({
        ...s,
        faviconImage: updated.faviconImage ?? '',
        faviconBaseUrl: updated.faviconBaseUrl ?? '',
      }));
      markThemeStale();
      snack.success(t('snackbar.faviconImageUploaded', 'Favicon uploaded.'));
    } catch (err) {
      if (err instanceof ApiError) setFaviconUploadError(err);
    } finally {
      setFaviconUploadPending(false);
    }
  }

  async function onUploadHero(file: File) {
    if (!brand) return;
    setHeroUploadPending(true);
    setHeroUploadError(null);
    try {
      const updated = await apiAdmin.brands.uploadHeroImage(brand.id, file);
      setState((s) => ({
        ...s,
        heroImage: updated.heroImage ?? '',
        heroBaseUrl: updated.heroBaseUrl ?? '',
      }));
      markThemeStale();
      snack.success(t('snackbar.heroImageUploaded', 'Hero image uploaded.'));
    } catch (err) {
      if (err instanceof ApiError) setHeroUploadError(err);
    } finally {
      setHeroUploadPending(false);
    }
  }

  function onPreview() {
    // Serialize non-sensitive brand display config to localStorage
    // Spec 0015 — use composed URLs from the brand DTO (override-aware)
    const previewConfig = {
      displayName: state.displayName,
      logoUrl: brand?.logoUrl || undefined,
      faviconUrl: brand?.faviconUrl || undefined,
      colors: state.colors,
      fonts: state.fonts,
      template: state.template || undefined,
      tagline: state.tagline || undefined,
      heroCopy: state.heroCopy || undefined,
      heroImageUrl: brand?.heroImageUrl || undefined,
      primaryColorHover: state.primaryColorHover || undefined,
      authProviders: state.authProviders,
      signupEnabled: state.signupEnabled,
      passwordResetEnabled: state.passwordResetEnabled,
      // Spec 0030 — guest-signup toggle + effective label (from the product).
      guestSignupEnabled: state.guestSignupEnabled,
      guestSignupLabel: effectiveProduct?.guestSignupLabel || undefined,
    };
    localStorage.setItem('brand-login-preview', JSON.stringify(previewConfig));
    window.open('/brands/preview', '_blank');
  }

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.home', 'Home'), to: '/' },
          { label: t('nav.brands', 'Brands'), to: '/brands' },
          { label: isNew ? t('brands.editor.newTitle', 'New brand') : (brand?.displayName ?? '') },
        ]}
      />
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h1">
          {isNew ? t('brands.editor.newTitle', 'New brand') : brand?.displayName}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={() => navigate('/brands')}>{t('common.cancel', 'Cancel')}</Button>
          {env.brandBundle && brand ? (
            <>
              <Button
                variant="outlined"
                onClick={() => void onExportBundle()}
                disabled={bundleExporting}
                startIcon={bundleExporting ? <CircularProgress size={16} /> : null}
              >
                {t('brands.bundle.export', 'Export bundle')}
              </Button>
              <Button variant="outlined" onClick={() => setBundleDialogOpen(true)}>
                {t('brands.bundle.import', 'Import bundle')}
              </Button>
            </>
          ) : null}
          <Button variant="outlined" onClick={onPreview}>
            {t('brands.editor.preview', 'Preview')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void onSave()}
            disabled={pending}
            startIcon={pending ? <CircularProgress size={16} /> : null}
          >
            {t('common.save', 'Save')}
          </Button>
        </Stack>
      </Stack>
      <ErrorBanner error={error} />
      {/* Spec 0020 Part A — Top row: Product · Slug · Display name · Login template */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select
              fullWidth
              required
              label={t('brands.fields.product', 'Product')}
              value={state.productId === '' ? '' : String(state.productId)}
              onChange={(e) => update('productId', Number(e.target.value))}
              disabled={!isNew}
              helperText={!isNew ? "Can't be changed after create" : ''}
            >
              {products.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.displayName} ({p.slug})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              required
              label={t('brands.fields.slug', 'Slug')}
              value={state.slug}
              onChange={(e) => update('slug', e.target.value.toLowerCase())}
              disabled={!isNew}
              helperText={!isNew ? "Can't be changed after create" : ''}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              required
              label={t('brands.fields.displayName', 'Display name')}
              value={state.displayName}
              onChange={(e) => update('displayName', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select
              fullWidth
              label={t('brands.fields.template', 'Login template')}
              value={state.template}
              onChange={(e) => update('template', e.target.value as BrandTemplate | '')}
              helperText={t('brands.fields.templateHelper', 'Absent → centered (default)')}
            >
              <MenuItem value="">
                <em>{t('brands.fields.templateDefault', 'Default (centered)')}</em>
              </MenuItem>
              {BRAND_TEMPLATES.map((tpl) => (
                <MenuItem key={tpl} value={tpl}>
                  {t(`brands.templates.${tpl}`, tpl)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>
      {/* Spec 0020 Part B — Tabs: Preview (default) + Advanced */}
      <BrandEditorTabs
        state={state}
        setState={setState}
        brand={brand}
        products={products}
        onUploadHero={onUploadHero}
        heroUploadPending={heroUploadPending}
        heroUploadError={heroUploadError}
        tenantsUsingTotal={tenantsUsingTotal ?? 0}
        themeStaleToken={themeStaleToken}
        onThemeSaved={() => void onThemeSaved()}
        copyLocalesStaleToken={copyLocalesStaleToken}
        onTranslationsSaved={markThemeStale}
        advancedContent={
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 3 }}>
                {/* Spec 0018 Part D — Section 2: Login template & marketing (minus template picker, moved to top row) */}
                <Typography variant="h3" gutterBottom>
                  {t('brands.editor.loginTemplateMarketing', 'Login template & marketing')}
                </Typography>
                <Grid container spacing={2}>
                  {/* Always-used fields — short name, login title, guest booking */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label={t('brands.fields.shortName', 'Product short name')}
                      value={state.shortName}
                      onChange={(e) => update('shortName', e.target.value)}
                      helperText={t(
                        'brands.fields.shortNameHelper',
                        'Used in the app bar, tab title, and installed-app name.',
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label={t('brands.fields.loginTitle', 'Login title')}
                      value={state.loginTitle}
                      onChange={(e) => update('loginTitle', e.target.value)}
                      placeholder={t('brands.placeholders.loginTitle', 'Sign in to {short name}')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label={t('brands.fields.welcomeHeadline', 'Welcome headline')}
                      value={state.welcomeHeadline}
                      onChange={(e) => update('welcomeHeadline', e.target.value)}
                    />
                  </Grid>

                  {/* Spec 0018 Part B — conditionally render used fields with placeholders */}
                  {(() => {
                    const usedFields = TEMPLATE_FIELDS[state.template || ''];
                    const unusedFields: TemplateField[] = (
                      ['tagline', 'heroCopy', 'location', 'heroImage'] as TemplateField[]
                    ).filter((f) => !usedFields.includes(f));

                    return (
                      <>
                        {/* Used fields — render normally */}
                        {usedFields.includes('tagline') && (
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              label={t('brands.fields.tagline', 'Tagline')}
                              value={state.tagline}
                              onChange={(e) => update('tagline', e.target.value)}
                              placeholder={t('brands.placeholders.tagline', 'Bookings made simple')}
                            />
                          </Grid>
                        )}
                        {usedFields.includes('heroCopy') && (
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              label={t('brands.fields.heroCopy', 'Hero copy')}
                              value={state.heroCopy}
                              onChange={(e) => update('heroCopy', e.target.value)}
                              placeholder={t('brands.placeholders.heroCopy', 'Get started today.')}
                            />
                          </Grid>
                        )}
                        {usedFields.includes('location') && (
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              label={t('brands.fields.location', 'Location')}
                              value={state.location}
                              onChange={(e) => update('location', e.target.value)}
                              placeholder={t('brands.placeholders.location', 'San Francisco')}
                            />
                          </Grid>
                        )}

                        {/* Guest booking — always shown (SASADM-0154: the primary hover
                            colour moved to the Theme tab) */}
                        <Grid size={{ xs: 12 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={state.guestSignupEnabled}
                                onChange={(e) => update('guestSignupEnabled', e.target.checked)}
                              />
                            }
                            label={t(
                              'brands.fields.guestSignupEnabled',
                              "Show 'Continue as guest' link on the login page",
                            )}
                          />
                          <GuestSignupHint product={effectiveProduct} />
                        </Grid>

                        {/* Spec 0018 Part B — unused fields in collapsed expander */}
                        {unusedFields.length > 0 && (
                          <Grid size={{ xs: 12 }}>
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2">
                                  {t('brands.editor.unusedFields', 'Not used by this template')}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={2}>
                                  {unusedFields.includes('tagline') && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                      <TextField
                                        fullWidth
                                        label={t('brands.fields.tagline', 'Tagline')}
                                        value={state.tagline}
                                        onChange={(e) => update('tagline', e.target.value)}
                                        placeholder={t(
                                          'brands.placeholders.tagline',
                                          'Bookings made simple',
                                        )}
                                      />
                                    </Grid>
                                  )}
                                  {unusedFields.includes('heroCopy') && (
                                    <Grid size={{ xs: 12 }}>
                                      <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label={t('brands.fields.heroCopy', 'Hero copy')}
                                        value={state.heroCopy}
                                        onChange={(e) => update('heroCopy', e.target.value)}
                                        placeholder={t(
                                          'brands.placeholders.heroCopy',
                                          'Get started today.',
                                        )}
                                      />
                                    </Grid>
                                  )}
                                  {unusedFields.includes('location') && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                      <TextField
                                        fullWidth
                                        label={t('brands.fields.location', 'Location')}
                                        value={state.location}
                                        onChange={(e) => update('location', e.target.value)}
                                        placeholder={t(
                                          'brands.placeholders.location',
                                          'San Francisco',
                                        )}
                                      />
                                    </Grid>
                                  )}
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          </Grid>
                        )}
                      </>
                    );
                  })()}
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Spec 0018 Part D — Section 3: Brand images */}
                <Typography variant="h3" gutterBottom>
                  {t('brands.editor.brandImages', 'Brand images')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <BrandImageField
                      assetType="logo"
                      image={state.logoImage}
                      baseUrl={state.logoBaseUrl}
                      composedUrl={composeImageUrl(state.logoImage, state.logoBaseUrl)}
                      globalAssetBaseUrl={brand?.globalAssetBaseUrl ?? ''}
                      onChange={(image, baseUrl) => {
                        setState((s) => ({ ...s, logoImage: image, logoBaseUrl: baseUrl }));
                      }}
                      onUpload={onUploadLogo}
                      isNew={isNew}
                      uploadPending={logoUploadPending}
                      uploadError={logoUploadError}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <BrandImageField
                      assetType="favicon"
                      image={state.faviconImage}
                      baseUrl={state.faviconBaseUrl}
                      composedUrl={composeImageUrl(state.faviconImage, state.faviconBaseUrl)}
                      globalAssetBaseUrl={brand?.globalAssetBaseUrl ?? ''}
                      onChange={(image, baseUrl) => {
                        setState((s) => ({ ...s, faviconImage: image, faviconBaseUrl: baseUrl }));
                      }}
                      onUpload={onUploadFavicon}
                      isNew={isNew}
                      uploadPending={faviconUploadPending}
                      uploadError={faviconUploadError}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <BrandImageField
                      assetType="hero"
                      image={state.heroImage}
                      baseUrl={state.heroBaseUrl}
                      composedUrl={composeImageUrl(state.heroImage, state.heroBaseUrl)}
                      globalAssetBaseUrl={brand?.globalAssetBaseUrl ?? ''}
                      onChange={(image, baseUrl) => {
                        setState((s) => ({ ...s, heroImage: image, heroBaseUrl: baseUrl }));
                      }}
                      onUpload={onUploadHero}
                      isNew={isNew}
                      uploadPending={heroUploadPending}
                      uploadError={heroUploadError}
                    />
                    {/* Spec 0018 Part D — hero template-awareness note when unused */}
                    {!TEMPLATE_FIELDS[state.template || ''].includes('heroImage') && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                          mt: 0.5,
                        }}
                      >
                        {t('brands.editor.heroNotUsed', 'Not used by the {template} template', {
                          template: state.template || 'centered',
                        })}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {/* SASADM-0156 — the new image slots, behind VITE_BRAND_ASSET_SLOTS (off) */}
                {env.brandAssetSlots && brand ? (
                  <Box sx={{ mt: 3 }}>
                    <BrandAssetSlots key={brand.id} brandId={brand.id} onChanged={markThemeStale} />
                  </Box>
                ) : null}

                <Divider sx={{ my: 3 }} />

                {/* Spec 0018 Part D — Section 4: Appearance */}
                <Typography variant="h3" gutterBottom>
                  {t('brands.editor.appearance', 'Appearance')}
                </Typography>
                {/* SASADM-0154 — colours and fonts are edited only in the Theme tab */}
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t(
                    'brands.theme.appearanceMoved',
                    'Colours and fonts are edited in the Theme tab.',
                  )}
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* Spec 0018 Part D — Section 5: Copy & providers (freeform only) */}
                <Typography variant="h3" gutterBottom>
                  {t('brands.editor.copyAndProviders', 'Copy & providers')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 1.5,
                  }}
                >
                  {t(
                    'brands.editor.copyAndProvidersHint',
                    'Login title, welcome headline, product short name, tagline, location and hero copy are edited in the Login template & marketing section.',
                  )}
                </Typography>
                <Stack spacing={1.5}>
                  {state.copyEntries.map((entry, idx) => {
                    const trimmedKey = entry.key.trim();
                    const isReserved = (RESERVED_COPY_KEYS as readonly string[]).includes(
                      trimmedKey,
                    );
                    return (
                      <Stack key={idx} direction="row" spacing={1}>
                        <TextField
                          label="Key"
                          value={entry.key}
                          onChange={(e) => setCopyEntry(idx, e.target.value, entry.value)}
                          sx={{ minWidth: 200 }}
                          error={isReserved}
                          helperText={
                            isReserved
                              ? t(
                                  'brands.editor.reservedKeyHelper',
                                  'Managed in the Login template & marketing section above.',
                                )
                              : ''
                          }
                        />
                        <TextField
                          fullWidth
                          label="Value"
                          value={entry.value}
                          onChange={(e) => setCopyEntry(idx, entry.key, e.target.value)}
                        />
                        <IconButton
                          aria-label="Remove"
                          onClick={() =>
                            update(
                              'copyEntries',
                              state.copyEntries.filter((_, i) => i !== idx),
                            )
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    );
                  })}
                  <Box>
                    <Button
                      onClick={() =>
                        update('copyEntries', [...state.copyEntries, { key: '', value: '' }])
                      }
                    >
                      Add copy string
                    </Button>
                  </Box>
                </Stack>

                <Divider sx={{ my: 3 }} />

                {/* Spec 0018 Part D — Section 6: Auth providers */}
                <Typography variant="h3" gutterBottom>
                  {t('brands.editor.authProviders', 'Auth providers')}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {t('brands.fields.authProviders', 'Auth providers')}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {SUPPORTED_PROVIDERS.map((p) => (
                      <FormControlLabel
                        key={p}
                        control={
                          <Checkbox
                            checked={state.authProviders.includes(p)}
                            onChange={(e) => toggleProvider(p, e.target.checked)}
                          />
                        }
                        label={p}
                      />
                    ))}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={state.signupEnabled}
                        onChange={(e) => update('signupEnabled', e.target.checked)}
                      />
                    }
                    label={t('brands.fields.signupEnabled', 'Signup enabled')}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={state.passwordResetEnabled}
                        onChange={(e) => update('passwordResetEnabled', e.target.checked)}
                      />
                    }
                    label={t('brands.fields.passwordResetEnabled', 'Password reset enabled')}
                  />
                </Stack>
              </Paper>

              {!isNew && tenantsUsing.length > 0 ? (
                <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
                  <Typography variant="h3" gutterBottom>
                    {t('brands.editor.tenantsUsing', 'Tenants using this brand')}
                  </Typography>
                  <Stack spacing={0.5}>
                    {tenantsUsing.map((tn) => (
                      <Typography
                        key={tn.id}
                        component={RouterLink}
                        to={`/tenants/${tn.id}`}
                        variant="body2"
                      >
                        {tn.name} ({tn.slug})
                      </Typography>
                    ))}
                  </Stack>
                  {(tenantsUsingTotal ?? 0) > tenantsUsing.length ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      {t('brands.editor.tenantsUsingTruncated', 'Showing first {{n}} of {{total}}.', {
                        n: tenantsUsing.length,
                        total: tenantsUsingTotal ?? 0,
                      })}
                    </Typography>
                  ) : null}
                </Paper>
              ) : null}

              {!isNew ? (
                <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
                  <Typography variant="h3" gutterBottom>
                    {t('brands.editor.delete', 'Delete brand')}
                  </Typography>
                  {tenantsUsing.length > 0 ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {t('brands.editor.deleteBlocked', '')}
                    </Alert>
                  ) : null}
                  <Button
                    color="error"
                    variant="outlined"
                    disabled={tenantsUsing.length > 0}
                    onClick={() => setConfirmDelete(true)}
                  >
                    {t('common.delete', 'Delete')}
                  </Button>
                </Paper>
              ) : null}
            </Grid>
          </Grid>
        }
      />
      <ConfirmDialog
        open={confirmDelete}
        title={t('brands.editor.delete', 'Delete brand')}
        body={t('brands.editor.deleteBody', '', { name: brand?.displayName ?? '' })}
        severity="error"
        {...(brand?.displayName ? { requireTypedConfirmation: brand.displayName } : {})}
        busy={pending}
        onConfirm={() => void onDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
      {env.brandBundle && brand && bundleDialogOpen ? (
        <BrandBundleDialog
          brandId={brand.id}
          onClose={() => setBundleDialogOpen(false)}
          onApplied={onBundleApplied}
        />
      ) : null}
    </Box>
  );
}
